import React from 'react'
import { Card, Row, Col } from 'react-bootstrap'
import styled from 'styled-components'
import { FaSpinner } from 'react-icons/fa'
import SelectedTrainingSourceCard from '../../../SelectedTrainingSourceCard'
import SelectedValidationSourceCard from '../../../SelectedValidationSourceCard'
import { SelectedTagsCard } from '../../ModelGenerationView/components/SelectedTagsCard'
import ModelItem from '../../../styled/ModelSelect/ModelItem'
import { PerformanceCard } from '../../ModelsOverview/components'
import { Model } from '../../../../types/models/model'

interface Project {
  id: string
  name: string
  datasetid?: string
  annotation_type?: string
  [key: string]: any
}

const StyledCard = styled(Card)`
  background: #2a2a2a;
  border: 1px solid #3a3a3a;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`

const SectionCard = styled(Card)`
  background: #2a2a2a;
  border: 1px solid #3a3a3a;
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  .card-body {
    padding: 1.25rem;
  }
`

interface ModelTrainingCardProps {
  model: Model
  project: Project | undefined
  isRetraining: boolean
  selectedTrainingSources: string[]
  selectedValidationSources: string[]
  selectedTagIds: string[]
  totalTrainingImages: number
  totalTrainingAnnotations: number
  totalValidationImages: number
  totalValidationAnnotations: number
  onTrainingSourcesChange: (sources: string[]) => void
  onValidationSourcesChange: (sources: string[]) => void
  onTagSelectionChange: (tagIds: string[]) => void
}

export const ModelTrainingCard: React.FC<ModelTrainingCardProps> = ({
  model,
  project,
  isRetraining,
  selectedTrainingSources,
  selectedValidationSources,
  selectedTagIds,
  totalTrainingImages,
  totalTrainingAnnotations,
  totalValidationImages,
  totalValidationAnnotations,
  onTrainingSourcesChange,
  onValidationSourcesChange,
  onTagSelectionChange,
}) => {
  return (
    <StyledCard style={{ position: 'relative' }}>
      {(isRetraining || model?.state.toLowerCase() === 'training') && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(30,30,30,0.7)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
          }}
        >
          <FaSpinner
            className="mb-3"
            style={{
              fontSize: '2rem',
              color: '#fff',
              animation: 'spin 1s linear infinite',
            }}
          />
          <div className="text-light fw-bold fs-5">Model is training...</div>
        </div>
      )}
      <Card.Body>
        <Row className="g-4">
          <Col md={4}>
            <SectionCard>
              <Card.Body>
                <h6 className="text-light mb-3">Model Information</h6>
                {model && (
                  <>
                    <div style={{ marginTop: '1rem' }}>
                      <ModelItem
                        model={model}
                        project={project}
                        $minimized={true}
                        showButtons={false}
                        showDatasetSources={false}
                      />
                    </div>
                    <div className="mt-3">
                      <PerformanceCard model={model} small />
                    </div>
                  </>
                )}
              </Card.Body>
            </SectionCard>
          </Col>
          <Col md={4}>
            <SectionCard>
              <Card.Body>
                <div className="mb-5">
                  <SelectedTrainingSourceCard
                    selectedSources={selectedTrainingSources}
                    onSourcesChange={onTrainingSourcesChange}
                    readOnly={model?.state.toLowerCase() === 'training'}
                  />
                </div>
                <div className="mb-3">
                  <SelectedValidationSourceCard
                    selectedSources={selectedValidationSources}
                    onSourcesChange={onValidationSourcesChange}
                    readOnly={model?.state.toLowerCase() === 'training'}
                  />
                </div>
                <div className="mt-5">
                  <SelectedTagsCard
                    selectedTagIds={selectedTagIds}
                    onTagSelectionChange={onTagSelectionChange}
                    disabled={model?.state.toLowerCase() === 'training'}
                  />
                </div>
              </Card.Body>
            </SectionCard>
          </Col>
          <Col md={4}>
            <SectionCard>
              <Card.Body>
                <h6 className="text-light mb-3">Dataset Stats</h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <span className="fw-bold text-info">Training Source</span>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <span className="text-info">Total Images:</span>
                      <span className="fw-bold text-light fs-5" style={{ letterSpacing: '1px' }}>
                        {totalTrainingImages}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <span className="text-info">Total Annotations:</span>
                      <span className="fw-bold text-light fs-5" style={{ letterSpacing: '1px' }}>
                        {totalTrainingAnnotations}
                      </span>
                    </div>
                  </div>
                  {selectedValidationSources.length > 0 && (
                    <div className="mt-3">
                      <span className="fw-bold text-warning">Validation Source</span>
                      <div className="d-flex justify-content-between align-items-center mt-1">
                        <span className="text-warning">Total Images:</span>
                        <span className="fw-bold text-light fs-5" style={{ letterSpacing: '1px' }}>
                          {totalValidationImages}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-1">
                        <span className="text-warning">Total Annotations:</span>
                        <span className="fw-bold text-light fs-5" style={{ letterSpacing: '1px' }}>
                          {totalValidationAnnotations}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </SectionCard>
          </Col>
        </Row>
      </Card.Body>
    </StyledCard>
  )
}

export default ModelTrainingCard
